import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Compass } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useUserLocation from '../../hooks/useUserLocation';
import {
  guestConciergeApi,
  getCachedGuestConciergeStatus,
} from '../../services/supportChatService';
import ChatWidgetShell from './ChatWidgetShell';
import {
  createChatMessage,
  buildChatHistory,
  loadStoredChat,
  persistChat,
  formatChatDisplayText,
  playChatNotificationSound,
  setupChatNotificationSoundListeners,
} from '../../utils/chatWidgetUtils';
import {
  inferGuestConciergeHints,
  buildBrowseVendorsPath,
  buildGuestConciergeLocationOptions,
} from '../../utils/guestConciergeHints';

const STORAGE_KEY = 'guest-concierge-chat';
const AUTO_GREET_KEY = 'guest-concierge-auto-greeted';

const buildWelcomeText = (exampleQuery, regionDisplay) => {
  const regionHint = regionDisplay ? ` in **${regionDisplay}**` : ' in your area';
  return `Welcome! I'm your booking concierge.

Tell me your **city** and **service** (e.g. "${exampleQuery}") and I'll show matching vendors${regionHint} you can book right away.

Try a quick option below, or describe your event in the box.`;
};

const buildWelcomeMessage = (locationOptions) =>
  createChatMessage(
    buildWelcomeText(locationOptions.exampleQuery, locationOptions.regionDisplay),
    'bot',
    {
    isWelcome: true,
    actions: locationOptions.welcomeActions,
  });

const hasPriorConversation = (messages) =>
  messages.some((message) => message.sender === 'user');

const GuestConciergeWidget = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { userLocation } = useUserLocation();

  const locationOptions = useMemo(
    () => buildGuestConciergeLocationOptions(userLocation),
    [
      userLocation?.city,
      userLocation?.country,
      userLocation?.state,
      userLocation?.display,
    ]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [assistantLabel, setAssistantLabel] = useState('Booking Concierge');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);
  const [hints, setHints] = useState({});

  const abortRef = useRef(null);
  const autoGreetDoneRef = useRef(false);
  const statusInitStartedRef = useRef(false);

  useEffect(() => setupChatNotificationSoundListeners(), []);

  const openConcierge = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  useEffect(() => {
    if (authLoading || isAuthenticated) return;
    if (statusInitStartedRef.current) return;
    statusInitStartedRef.current = true;

    let cancelled = false;

    const init = async () => {
      let enabled = true;
      let label = 'Booking Concierge';
      try {
        const res = await guestConciergeApi.getStatus();
        enabled = Boolean(res?.data?.enabled);
        label = res?.data?.assistant_name || label;
      } catch (err) {
        const cached = getCachedGuestConciergeStatus();
        if (cached) {
          enabled = Boolean(cached?.data?.enabled);
          label = cached?.data?.assistant_name || label;
        } else {
          enabled = err?.response?.status !== 429;
        }
        if (err?.response?.status === 429 && !cached) {
          setError('Chat is busy — please wait a moment and try again.');
        }
      }
      if (cancelled) return;

      const opts = buildGuestConciergeLocationOptions(userLocation);
      const stored = loadStoredChat(
        STORAGE_KEY,
        buildWelcomeText(opts.exampleQuery, opts.regionDisplay)
      );
      const resumeExistingChat = hasPriorConversation(stored);

      setChatEnabled(enabled);
      setAssistantLabel(label);

      if (resumeExistingChat) {
        setMessages(
          stored.map((message) => ({
            ...message,
            text: message.sender === 'bot' ? formatChatDisplayText(message.text) : message.text,
          }))
        );
      } else {
        setMessages([buildWelcomeMessage(opts)]);
      }

      setStatusChecked(true);

      const alreadyGreeted = sessionStorage.getItem(AUTO_GREET_KEY) === '1';
      if (!resumeExistingChat && !alreadyGreeted && !autoGreetDoneRef.current) {
        autoGreetDoneRef.current = true;
        sessionStorage.setItem(AUTO_GREET_KEY, '1');
        window.setTimeout(() => {
          openConcierge();
          playChatNotificationSound({ pendingIfBlocked: true });
        }, 900);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, openConcierge]);

  useEffect(() => {
    if (!statusChecked || hasPriorConversation(messages)) return;
    setMessages([buildWelcomeMessage(locationOptions)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh welcome chips when geolocation resolves
  }, [locationOptions.cityDisplay, statusChecked]);

  useEffect(() => {
    if (!isAuthenticated) {
      persistChat(STORAGE_KEY, messages);
    }
  }, [messages, isAuthenticated]);

  useEffect(() => {
    const open = () => openConcierge();
    window.addEventListener('openGuestConcierge', open);
    window.addEventListener('openChatWidget', open);
    return () => {
      window.removeEventListener('openGuestConcierge', open);
      window.removeEventListener('openChatWidget', open);
    };
  }, [openConcierge]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const inferHints = useCallback(
    (text, prev) => inferGuestConciergeHints(text, prev, locationOptions.citySlug),
    [locationOptions.citySlug]
  );

  const sendToConcierge = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isSending) return;

    if (!chatEnabled) {
      setError('AI concierge is not configured. Add GEMINI_API_KEY to the API server.');
      return;
    }

    const isNearMePrompt =
      trimmed === locationOptions.servicesPrompt ||
      /\bnear me\b/i.test(trimmed);

    const messageForApi =
      isNearMePrompt && locationOptions.citySlug
        ? `I need services in ${locationOptions.regionDisplay || locationOptions.cityDisplay || locationOptions.citySlug}`
        : trimmed;

    const nextHints = inferHints(messageForApi, hints);
    if (isNearMePrompt && locationOptions.citySlug) {
      nextHints.city = locationOptions.citySlug;
    }
    setHints(nextHints);
    setError(null);
    setMessages((prev) => [...prev, createChatMessage(trimmed, 'user')]);
    setInputValue('');
    setIsSending(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await guestConciergeApi.sendMessage({
        message: messageForApi,
        history: buildChatHistory(messages),
        hints: nextHints,
        signal: controller.signal,
      });

      const reply = formatChatDisplayText(res?.data?.reply);
      if (!reply) throw new Error('Empty response');

      const browsePath = buildBrowseVendorsPath(nextHints);
      const actions = (res?.data?.actions || []).map((action) =>
        action.path === '/browse-vendors' && nextHints.city
          ? { ...action, path: browsePath }
          : action
      );

      setMessages((prev) => [
        ...prev,
        createChatMessage(reply, 'bot', {
          actions,
          suggestions: res?.data?.suggestions || [],
        }),
      ]);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      setError(err?.response?.data?.message || err?.message || 'Something went wrong.');
      setMessages((prev) => [
        ...prev,
        createChatMessage(
          "I couldn't find recommendations right now. Browse vendors or create an account to continue.",
          'bot',
          {
            isError: true,
            actions: [
              {
                label: locationOptions.cityDisplay
                  ? `Browse vendors in ${locationOptions.cityDisplay}`
                  : 'Browse matching vendors',
                path: buildBrowseVendorsPath({
                  city: locationOptions.citySlug || nextHints.city,
                }),
                type: 'link',
              },
            ],
          }
        ),
      ]);
    } finally {
      setIsSending(false);
      abortRef.current = null;
    }
  };

  const handleClear = () => {
    abortRef.current?.abort();
    setIsSending(false);
    setError(null);
    setHints(locationOptions.citySlug ? { city: locationOptions.citySlug } : {});
    setMessages([buildWelcomeMessage(locationOptions)]);
    sessionStorage.removeItem(STORAGE_KEY);
    openConcierge();
  };

  const showQuickPrompts =
    !messages.some((message) => message.sender === 'user') && !isSending;

  if (authLoading || isAuthenticated || !statusChecked) {
    return null;
  }

  return (
    <ChatWidgetShell
      variant="concierge"
      isOpen={isOpen}
      isMinimized={isMinimized}
      chatEnabled={chatEnabled}
      assistantLabel={assistantLabel}
      subtitle={chatEnabled ? 'Find & book the perfect service' : 'Offline'}
      messages={messages}
      inputValue={inputValue}
      onInputChange={setInputValue}
      onSend={sendToConcierge}
      isSending={isSending}
      error={error}
      onToggle={() => {
        if (isOpen && !isMinimized) setIsMinimized(true);
        else if (isOpen && isMinimized) setIsMinimized(false);
        else {
          openConcierge();
          playChatNotificationSound();
        }
      }}
      onClose={() => {
        abortRef.current?.abort();
        setIsOpen(false);
        setIsMinimized(false);
      }}
      onClear={handleClear}
      quickPrompts={showQuickPrompts ? locationOptions.quickPrompts : []}
      onQuickPrompt={sendToConcierge}
      openButtonLabel="Open booking concierge"
      fabIcon={Compass}
      fabClassName="bg-violet-900"
    />
  );
};

export default GuestConciergeWidget;
